// src/pages/Home.js - Part 1
import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Card, 
  CardMedia, 
  CardContent, 
  styled, 
  IconButton, 
  Modal, 
  Backdrop, 
  Fade 
} from '@mui/material';
import { 
  Close as CloseIcon, 
  KeyboardArrowDown, 
  Star, 
  FormatQuote,
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  WhatsApp
} from '@mui/icons-material';
import images from '../assets/images';
// Remove all Swiper-related imports and add these instead
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";


// Styled Components
const HeroContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  position: 'relative',
  backgroundImage: `url(${images.main})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  color: 'white',
  textAlign: 'center',
  animation: 'fadeIn 1.5s ease-out',
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}));

const ScrollIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 40,
  left: '50%',
  transform: 'translateX(-50%)',
  cursor: 'pointer',
  animation: 'bounce 2s infinite',
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
    '40%': { transform: 'translateY(-30px)' },
    '60%': { transform: 'translateY(-15px)' },
  },
}));

const ImageModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: 'transparent',
  outline: 'none',
  maxWidth: '95vw',
  maxHeight: '90vh',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    margin: theme.spacing(2),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-10px)',
  },
}));

const ReviewCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(10px)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-10px)',
  },
}));
// src/pages/Home.js - Part 2

// Data Constants
const featuredRooms = [
  {
    id: 1,
    title: 'Garden Villa',
    image: images.main,
    description: 'Spacious villa surrounded by lush gardens',
    price: 'Weekdays : ₹2500/night Weekends : ₹3000/night',
    features: ['A/C Room', 'King Size Bed', 'Private Balcony', 'Garden View', 'Free WiFi'],
  },
];

const amenities = [
  {
    title: 'Pool',
    image: images.pool,
    description: 'Swim in our stunning pool overlooking the forest',
  },
  {
    title: 'Badminton Court',
    image: images.badminton,
    description: 'Badminton courts for players of all levels.',
  },
  {
    title: 'Sports Facilities',
    image: images.sports,
    description: 'Modern sports facilities for a variety of activities.',
  },
  {
    title: 'Party Halls',
    image: images.party,
    description: 'Elegant party hall for memorable celebrations.',
  },
  {
    title: 'Camp Fire',
    image: images.camp,
    description: 'Cozy campfire spots for unforgettable gatherings under the stars.',
  },
  {
    title: 'Kids Pool',
    image: images.kids,
    description: 'Safe and fun kids pool designed for endless splashes and smiles.',
  },
];

const resortImages = [
  images.pool,
  images.fire,
  images.path,
  images.swing,
  images.pool1,
  images.nightsit,
];

const customerReviews = [
  {
    id: 1,
    name: "Ebin Muttath",
    rating: 4,
    date: "September 2024",
    comment: "Budget friendly... inside rubber forest,good pool... servicing needed improvement",
    image: images.ebin,
  },
  {
    
    id: 2,
    name: "Jimmy Joy",
    rating: 5,
    date: "June 2024",
    comment: "Nice ambience and facilities here. Away from the hustle and bustle of the city so it is very peaceful. The place has a swimming pool with a fountain, campfire area, outdoor eating area and carpark area as well. The rooms we stayed in was new and very nice.",
    image: images.jimmy,
  },
 
  {
    id: 3,
    name: "Robin Josy",
    rating: 5,
    date: "December 2023",
    comment: "A hidden gem! The perfect blend of luxury and nature. The staff went above and beyond to make our stay special.",
    image: images.robin,
  },
];

// Hero Section Component
function HeroSection() {
  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <HeroContainer>
      <ContentWrapper>
        <Box sx={{
          animation: 'fadeIn 1s ease-out',
          '@keyframes fadeIn': {
            from: { 
              opacity: 0,
              transform: 'translateY(30px)'
            },
            to: { 
              opacity: 1,
              transform: 'translateY(0)'
            },
          },
        }}>
          <Typography variant="h1" sx={{
            fontSize: { xs: '2.5rem', md: '4rem' },
            fontWeight: 700,
            mb: 2,
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
          }}>
            Welcome to East Fort Holidays
          </Typography>
          <Typography variant="h4" sx={{
            fontSize: { xs: '1.2rem', md: '1.5rem' },
            mb: 4,
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
          }}>
            Take A Break
          </Typography>
        </Box>
      </ContentWrapper>
      <ScrollIndicator onClick={scrollToContent}>
        <KeyboardArrowDown sx={{ fontSize: '2rem', color: 'white' }} />
      </ScrollIndicator>
    </HeroContainer>
  );
}
// src/pages/Home.js - Part 3

function Home() {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  return (
    <Box>
      <HeroSection />

      {/* Resort Gallery Section */}
      <Box sx={{ py: 12 }}>
        <Container>
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 8 }}>
            Discover Our Paradise
          </Typography>
          <Grid container spacing={2}>
            {resortImages.map((image, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Box sx={{
                  opacity: 0,
                  animation: `fadeIn 0.8s ease-out ${index * 0.2}s forwards`,
                  '@keyframes fadeIn': {
                    from: { 
                      opacity: 0,
                      transform: 'translateY(20px)'
                    },
                    to: { 
                      opacity: 1,
                      transform: 'translateY(0)'
                    },
                  },
                }}>
                  <Box
                    sx={{
                      position: 'relative',
                      height: '400px',
                      overflow: 'hidden',
                      borderRadius: 2,
                      cursor: 'pointer',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      '&:hover img': { transform: 'scale(1.1)' },
                    }}
                    onClick={() => handleImageClick(image)}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt={`Resort view ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.8s ease',
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Image Modal */}
      <ImageModal
        open={Boolean(selectedImage)}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={Boolean(selectedImage)}>
          <ModalContent>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="img"
              src={selectedImage}
              alt="Selected view"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          </ModalContent>
        </Fade>
      </ImageModal>

      {/* Accommodations Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 12 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 8 }}>
            Accommodations
          </Typography>
          <Grid container justifyContent="center">
            {featuredRooms.map((room) => (
              <Grid item key={room.id} xs={12} sm={10} md={8} lg={7}>
                <StyledCard sx={{ boxShadow: '0 8px 40px rgba(0,0,0,0.12)', borderRadius: '16px' }}>
                  <CardMedia
                    component="img"
                    height="400"
                    image={room.image}
                    alt={room.title}
                  />
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                      {room.title}
                    </Typography>
                    <Typography variant="body1" paragraph>{room.description}</Typography>
                    <Box sx={{ mb: 3 }}>
                      {room.features.map((feature, idx) => (
                        <Typography key={idx} variant="body1" sx={{ mb: 1 }}>
                          • {feature}
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 600, mb: 3 }}>
                      {room.price}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
{/* 
     {/* Customer Reviews Section */}
<Box 
  sx={{ 
    py: 6,
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    position: 'relative',
    overflow: 'hidden',
  }}
>
  <Container maxWidth="xl">
    <Typography 
      variant="h4"
      component="h2" 
      textAlign="center" 
      gutterBottom 
      sx={{ 
        mb: 4,
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          width: '40px',
          height: '3px',
          backgroundColor: 'primary.main',
          margin: '10px auto',
          borderRadius: '2px',
        }
      }}
    >
      What Our Guests Say
    </Typography>

    <Slider
      dots={false}
      infinite={true}
      speed={5000}
      slidesToShow={3}
      slidesToScroll={1}
      autoplay={true}
      autoplaySpeed={0}
      cssEase="linear"
      pauseOnHover={false}
      rtl={true}
      responsive={[
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
          }
        },
        {
          breakpoint: 640,
          settings: {
            slidesToShow: 1,
          }
        }
      ]}
    >
      {customerReviews.map((review) => (
        <div key={review.id}>
          <ReviewCard sx={{ height: '250px', p: 2, mx: 1 }}>
            <FormatQuote sx={{ fontSize: '40px', color: 'primary.main', opacity: 0.3, mb: 1 }} />
            <Box
              component="img"
              src={review.image}
              alt={review.name}
              sx={{ width: 50, height: 50, borderRadius: '50%', mb: 1 }}
            />
            <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>
              "{review.comment.substring(0, 100)}..."
            </Typography>
            <Box sx={{ mb: 1 }}>
              {[...Array(review.rating)].map((_, index) => (
                <Star key={index} sx={{ color: '#FFD700', fontSize: '1rem' }} />
              ))}
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {review.name}
            </Typography>
          </ReviewCard>
        </div>
      ))}
    </Slider>
  </Container>

  {/* Decorative Elements */}
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      opacity: 0.1,
      background: `
        radial-gradient(circle at 20% 20%, rgba(0,0,0,0.05) 0%, transparent 20%),
        radial-gradient(circle at 80% 80%, rgba(0,0,0,0.05) 0%, transparent 20%)
      `,
    }}
  />
</Box>
      {/* Amenities Section */}
      <Box sx={{ py: 12, bgcolor: 'background.default' }}>
        <Container>
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 8 }}>
            Resort Amenities
          </Typography>
          <Grid container spacing={4}>
            {amenities.map((amenity, index) => (
              <Grid item key={amenity.title} xs={12} sm={6} md={4}>
                <StyledCard>
                  <CardMedia
                    component="img"
                    height="250"
                    image={amenity.image}
                    alt={amenity.title}
                  />
                  <CardContent>
                    <Typography variant="h5" gutterBottom>{amenity.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {amenity.description}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;